import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import './ArchiveStyles.css';
import MetadataReview from './MetadataReview';
import {
  ARCHIVE_PROFILES,
  ARCHIVE_PROFILE_CREATE,
} from '../../auth/urlParameters';
import { Api } from '../../auth/Configs';
import { ToastError, ToastConfirm } from '../../../ui/AlertesUi';
import { RiAddLine, RiDeleteBin6Line } from 'react-icons/ri';
import { SimpleSpinner } from '../../../ui/Spinner';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

const initialProfile = {
  code: '',
  title: '',
  description: '',
  dateValidite: '',
  dateExpiration: '',
  producerServiceId: '',
  defaultPlanId: null,
  defaultRetentionRuleId: null,
  allowedFormats: [],
  maxFileSizeMb: null,
  confidentialite: '',
  status: 'draft',
  captureFields: [],
  version: 1,
  revisionHistory: [],
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  updatedBy: null,
};

const flattenTree = (nodes, depth = 0) =>
  (nodes || []).reduce((result, node) => {
    result.push({ node, depth });
    if (node.children?.length) result.push(...flattenTree(node.children, depth + 1));
    return result;
  }, []);

const ArchiveProfiles = () => {
  const session = useSelector((state) => state.sessionAuth);
  const { id } = useParams();
  const navigate = useNavigate();
  const profileId = useMemo(() => {
    const parsed = Number(id);
    return Number.isNaN(parsed) ? null : parsed;
  }, [id]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const [profile, setProfile] = useState(initialProfile);
  const [selectedId, setSelectedId] = useState(null);
  const [ocrEnabled, setOcrEnabled] = useState(true);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canManage = useMemo(() => {
    const roles = Array.isArray(session.roles) ? session.roles : [];
    return ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].some((role) => roles.includes(role));
  }, [session.roles]);

  const serviceOptions = useMemo(() => flattenTree(services), [services]);

  const setProfileFromData = useCallback((data) => {
    setProfile({
      code: data.code || '',
      title: data.title || '',
      description: data.description || '',
      dateValidite: data.dateValidite ? data.dateValidite.substring(0, 10) : '',
      dateExpiration: data.dateExpiration ? data.dateExpiration.substring(0, 10) : '',
      producerServiceId: data.producerServiceId || '',
      defaultPlanId: data.defaultPlanId || null,
      defaultRetentionRuleId: data.defaultRetentionRuleId || null,
      allowedFormats: data.allowedFormats || [],
      maxFileSizeMb: data.maxFileSizeMb || null,
      confidentialite: data.confidentialite || '',
      status: data.status || 'draft',
      captureFields: data.captureFields || [],
      version: data.version || 1,
      revisionHistory: data.revisionHistory || [],
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null,
      createdBy: data.createdBy || null,
      updatedBy: data.updatedBy || null,
    });
  }, []);

  const fetchProfile = useCallback(() => {
    if (profileId === null) {
      setSelectedId(null);
      setProfile(initialProfile);
      return Promise.resolve();
    }
    return axios
      .get(`${ARCHIVE_PROFILES()}/${profileId}`, authHeaders)
      .then((res) => {
        setSelectedId(res.data.id);
        setProfileFromData(res.data);
      })
      .catch(() => ToastError('Profil introuvable'));
  }, [authHeaders, profileId, setProfileFromData]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProfile(),
      axios.get(ARCHIVE_PROFILES(), { ...authHeaders, params: { limit: 1 } })
        .then((res) => setOcrEnabled(res.data?.ocrEnabled !== false)),
      axios.get(`${Api}/services`, authHeaders).then((res) => setServices(res.data?.services || [])),
    ])
      .catch(() => ToastError('Impossible de charger les référentiels du profil'))
      .finally(() => setLoading(false));
  }, [authHeaders, fetchProfile]);

  return (
    <>
      <Breadcrumb title="Archives" submenu="Profil d'archive" />
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
            <StickyBox offsetTop={20} offsetBottom={20}>
              <Sidebar />
            </StickyBox>
          </div>
          <div className="col-md-8 col-lg-9 col-xl-9">
            <div className="card">
              <div className="card-body">
                <div className="profile-form-header">
                  <div>
                    <h4 className="card-title d-flex align-items-center mb-1">
                      <i className="ri-profile-line me-2"></i>
                      {selectedId ? 'Modifier le profil d’archive' : 'Nouveau profil d’archive'}
                    </h4>
                    <p>Définissez les règles de description et de contrôle du profil.</p>
                  </div>
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={() => navigate('/apps/in/archives/profils')}
                  >
                    Retour aux profils
                  </button>
                </div>
                {loading ? SimpleSpinner() : (
                <form
                  className="profile-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!canManage) {
                      ToastError("Vous n'êtes pas autorisé à modifier les profils");
                      return;
                    }
                    if (profile.dateValidite && profile.dateExpiration
                      && profile.dateExpiration < profile.dateValidite) {
                      ToastError('La fin de validité doit être postérieure au début');
                      return;
                    }
                    const payload = {
                      ...profile,
                      documentType: Array.from(profile.title.trim()).slice(0, 64).join(''),
                      defaultPlanId: profile.defaultPlanId,
                      defaultRetentionRuleId: profile.defaultRetentionRuleId,
                      allowedFormats: profile.allowedFormats,
                      maxFileSizeMb: profile.maxFileSizeMb,
                      captureFields: profile.captureFields || [],
                    };
                    ['version', 'revisionHistory', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy']
                      .forEach((field) => delete payload[field]);
                    const request = selectedId
                      ? axios.patch(`${ARCHIVE_PROFILES()}/${selectedId}`, payload, authHeaders)
                      : axios.post(ARCHIVE_PROFILE_CREATE(), payload, authHeaders);
                    setSaving(true);
                    request
                      .then(() => {
                        ToastConfirm('Profil enregistré');
                        if (!selectedId) {
                          setProfile(initialProfile);
                          navigate('/apps/in/archives/profils');
                        } else {
                          fetchProfile();
                        }
                      })
                      .catch((err) => ToastError(err.response?.data?.message || 'Erreur enregistrement profil'))
                      .finally(() => setSaving(false));
                  }}
                >
                  {!canManage && (
                    <div className="alert alert-warning">
                      Consultation uniquement : la modification est réservée aux archivistes et administrateurs.
                    </div>
                  )}
                  <fieldset disabled={!canManage || saving}>
                  <h5 className="profile-form-section-title">Identification</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Code *</label>
                        <input
                          className="form-control"
                          value={profile.code}
                          onChange={(e) => setProfile({ ...profile, code: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Titre *</label>
                        <input
                          className="form-control"
                          value={profile.title}
                          onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={profile.description}
                          onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Statut *</label>
                        <select
                          className="form-control"
                          value={profile.status}
                          onChange={(e) => setProfile({ ...profile, status: e.target.value })}
                          required
                        >
                          <option value="draft">Brouillon</option>
                          <option value="active">Actif</option>
                          <option value="retired">Retiré</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <h5 className="profile-form-section-title">Gouvernance</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Service producteur *</label>
                        <select
                          className="form-control"
                          value={profile.producerServiceId}
                          onChange={(e) => setProfile({ ...profile, producerServiceId: e.target.value })}
                          required
                        >
                          <option value="">Choisir un service</option>
                          {serviceOptions.map(({ node, depth }) => (
                            <option key={node.id} value={node.id}>
                              {'— '.repeat(depth)}{node.code} — {node.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Niveau de confidentialité *</label>
                        <select
                          className="form-control"
                          value={profile.confidentialite}
                          onChange={(e) => setProfile({ ...profile, confidentialite: e.target.value })}
                          required
                        >
                          <option value="">Choisir</option>
                          <option value="Interne">Interne</option>
                          <option value="Restreint">Restreint</option>
                          <option value="Confidentiel">Confidentiel</option>
                          <option value="Très Confidentiel">Très Confidentiel</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Début de validité</label>
                        <input
                          type="date"
                          className="form-control"
                          value={profile.dateValidite}
                          onChange={(e) => setProfile({ ...profile, dateValidite: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Fin de validité</label>
                        <input
                          type="date"
                          className="form-control"
                          min={profile.dateValidite || undefined}
                          value={profile.dateExpiration}
                          onChange={(e) => setProfile({ ...profile, dateExpiration: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {selectedId && (
                    <div className="profile-audit-summary" aria-label="Traçabilité du profil">
                      <div>
                        <span>Version</span>
                        <strong>{profile.version || 1}</strong>
                      </div>
                      <div>
                        <span>Créé le</span>
                        <strong>{formatAuditDate(profile.createdAt)}</strong>
                      </div>
                      <div>
                        <span>Dernière modification</span>
                        <strong>{formatAuditDate(profile.updatedAt)}</strong>
                      </div>
                      <div>
                        <span>Modifié par</span>
                        <strong>{profile.updatedBy?.username || profile.createdBy?.username || 'Système'}</strong>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-12">
                      <h5 className="profile-form-section-title">Métadonnées et captation OCR</h5>
                      {!ocrEnabled && (
                        <div className="alert alert-warning p-2">
                          OCR désactivé sur cet environnement : les métadonnées automatiques ne seront pas captées.
                        </div>
                      )}
                      <DynamicFieldsEditor
                        fields={profile.captureFields}
                        authHeaders={authHeaders} ocrEnabled={ocrEnabled}
                        onChange={(fields) => setProfile({ ...profile, captureFields: fields })}
                      />
                    </div>
                  </div>
                  </fieldset>
                  {selectedId && profile.revisionHistory?.length > 0 && (
                    <details className="profile-revision-history">
                      <summary>Historique des révisions ({profile.revisionHistory.length})</summary>
                      <div className="profile-revision-list">
                        {[...profile.revisionHistory].reverse().map((revision, index) => (
                          <div className="profile-revision-item" key={`${revision.version}-${revision.recordedAt}-${index}`}>
                            <strong>Version {revision.version}</strong>
                            <span>{formatAuditDate(revision.recordedAt)}</span>
                            <span>{revision.recordedBy?.username || 'Système'}</span>
                            <span>{revision.code} — {revision.title}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  <div className="d-flex gap-2 mt-2">
                    {canManage && (
                    <button className="btn btn-primary" type="submit" disabled={saving}>
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    )}
                  </div>
                </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArchiveProfiles;

const formatAuditDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR');
};

const slugifyKey = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_');

const emptyField = {
  label: '',
  required: false,
  readOnly: false,
  type: 'string',
  keywords: [],
  pattern: '',
  validationPattern: '',
  allowedValues: [],
  afterField: '',
};

const parseKeywords = (value) =>
  (value || '')
    .split(/[,;\n]+/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const formatKeywords = (keywords) =>
  Array.isArray(keywords) ? keywords.join(', ') : '';

const FIELD_TEMPLATES = [
  { label: 'Référence', type: 'string', keywords: ['Référence', 'Numéro'] },
  { label: 'Date du document', type: 'date', keywords: ['Date du document', 'Date de facture'] },
  { label: 'Montant', type: 'number', keywords: ['Total TTC', 'Montant total'] },
  { label: 'Organisation', type: 'string', keywords: ['Fournisseur', 'Organisation'] },
];

const FIELD_TYPE_LABELS = {
  string: 'Texte ou référence',
  number: 'Nombre / montant',
  date: 'Date',
  boolean: 'Oui / Non',
};

const FieldSettings = ({ field, fields = [], onChange, prefix }) => {
  const keywordsText = formatKeywords(field.keywords);
  const [keywordsDraft, setKeywordsDraft] = useState(keywordsText);
  const allowedValuesText = formatKeywords(field.allowedValues);
  const [allowedValuesDraft, setAllowedValuesDraft] = useState(allowedValuesText);
  useEffect(() => { setKeywordsDraft(keywordsText); }, [keywordsText]);
  useEffect(() => { setAllowedValuesDraft(allowedValuesText); }, [allowedValuesText]);
  return <>
  <div className="row">
    <div className="col-md-6 form-group"><label htmlFor={`${prefix}-label`}>Nom de la métadonnée</label><input id={`${prefix}-label`} className="form-control" value={field.label} placeholder="Ex. : Fournisseur" onChange={(e) => onChange({ label: e.target.value })} /></div>
    <div className="col-md-6 form-group"><label htmlFor={`${prefix}-type`}>Valeur attendue</label><select id={`${prefix}-type`} className="form-control" value={field.type || 'string'} onChange={(e) => onChange({ type: e.target.value, ...(e.target.value !== 'string' ? { allowedValues: [] } : {}) })}>
      <option value="string">Texte ou référence</option><option value="number">Nombre / montant</option><option value="date">Date</option><option value="boolean">Oui / Non</option>
    </select></div>
  </div>
  <label htmlFor={`${prefix}-keywords`}>Libellés à rechercher dans le document</label>
  <input id={`${prefix}-keywords`} className="form-control" placeholder="Ex. : Fournisseur, Émetteur" value={keywordsDraft} onChange={(e) => setKeywordsDraft(e.target.value)} onBlur={() => onChange({ keywords: parseKeywords(keywordsDraft) })} />
  <p className="archive-create-hint">Séparez les libellés par des virgules. La valeur sera recherchée après le libellé.</p>
  <label><input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ required: e.target.checked })} /> Obligatoire à l’enregistrement de l’archive</label>
  <details className="metadata-advanced"><summary>Réglages avancés</summary>
    {field.key && <p>Identifiant : {field.key}</p>}
    <label htmlFor={`${prefix}-pattern`}>Expression régulière (facultatif)</label><input id={`${prefix}-pattern`} className="form-control" maxLength={500} value={field.pattern || ''} onChange={(e) => onChange({ pattern: e.target.value })} />
    <small>Le premier groupe capturé fournit la valeur. Testez la règle sur un document avant utilisation.</small>
    <label htmlFor={`${prefix}-validation-pattern`}>Format de la valeur (facultatif)</label><input id={`${prefix}-validation-pattern`} className="form-control" maxLength={500} value={field.validationPattern || ''} placeholder="Ex. : ^[A-Z][0-9]{9}$" onChange={(e) => onChange({ validationPattern: e.target.value })} />
    <small>Cette règle contrôle la valeur enregistrée, indépendamment de l’extraction OCR.</small>
    {field.type === 'string' && <><label htmlFor={`${prefix}-allowed-values`}>Valeurs autorisées (facultatif)</label><input id={`${prefix}-allowed-values`} className="form-control" value={allowedValuesDraft} placeholder="Ex. : Original, Copie, Certifié" onChange={(e) => setAllowedValuesDraft(e.target.value)} onBlur={() => onChange({ allowedValues: parseKeywords(allowedValuesDraft) })} /><small>Séparez les valeurs par des virgules. Une valeur différente sera signalée comme non conforme.</small></>}
    {field.type === 'date' && <><label htmlFor={`${prefix}-after-field`}>Date au plus tôt égale à</label><select id={`${prefix}-after-field`} className="form-control" value={field.afterField || ''} onChange={(e) => onChange({ afterField: e.target.value })}><option value="">Aucune comparaison</option>{fields.filter((candidate) => candidate.key && candidate.key !== field.key && candidate.type === 'date').map((candidate) => <option key={candidate.key} value={candidate.key}>{candidate.label || candidate.key}</option>)}</select></>}
  </details>
</>;
};

const DynamicFieldsEditor = ({ fields = [], onChange, authHeaders, ocrEnabled }) => {
  const [newField, setNewField] = useState(emptyField);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [expandedFieldKey, setExpandedFieldKey] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testedFields, setTestedFields] = useState('');
  const openAddModal = (template = emptyField) => {
    setNewField({ ...emptyField, ...template });
    setAddModalOpen(true);
  };
  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewField(emptyField);
  };
  const addField = () => {
    const label = newField.label.trim();
    const key = slugifyKey(label);
    if (!key) { ToastError('Indiquez un nom de métadonnée valide'); return; }
    if (fields.some((field) => field.key === key)) { ToastError('Cette métadonnée existe déjà'); return; }
    onChange([...fields, { ...newField, label, key, readOnly: false }]);
    closeAddModal();
  };
  const testCapture = async () => {
    setLoading(true); setError(''); setResult(null);
    const snapshot = JSON.stringify(fields);
    const data = new FormData(); data.append('file', file); data.append('captureFields', snapshot);
    try {
      const response = await axios.post(`${ARCHIVE_PROFILES()}/capture/preview`, data, authHeaders);
      setResult(response.data); setTestedFields(snapshot);
    } catch (err) { setError(err.response?.data?.message || 'Impossible d’analyser le document.'); }
    finally { setLoading(false); }
  };
  return <div className="metadata-editor">
    <p>Définissez les informations à récupérer, puis vérifiez leur extraction sur un document exemple.</p>
    <div className="table-responsive metadata-table-wrap">
      <table className="table table-hover metadata-table syidocs-data-table">
        <thead>
          <tr>
            <th scope="col">N°</th>
            <th scope="col">Métadonnée</th>
            <th scope="col">Valeur attendue</th>
            <th scope="col">Libellés recherchés</th>
            <th scope="col">Obligatoire</th>
            <th scope="col" className="metadata-table-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => {
            const expanded = expandedFieldKey === field.key;
            return <Fragment key={field.key}>
              <tr>
                <td>{index + 1}</td>
                <td>
                  <strong>{field.label || 'Sans nom'}</strong>
                  <small className="metadata-table-key">{field.key}</small>
                </td>
                <td>{FIELD_TYPE_LABELS[field.type] || FIELD_TYPE_LABELS.string}</td>
                <td className="metadata-table-keywords">{formatKeywords(field.keywords) || '—'}</td>
                <td><span className={`metadata-required ${field.required ? 'is-required' : ''}`}>{field.required ? 'Oui' : 'Non'}</span></td>
                <td>
                  <div className="metadata-row-actions">
                    <button
                      type="button"
                      className="syidocs-table-icon-button"
                      aria-label={`${expanded ? 'Fermer la modification de' : 'Modifier'} ${field.label || 'la métadonnée'}`}
                      title={expanded ? 'Fermer' : 'Modifier'}
                      aria-expanded={expanded}
                      aria-controls={`metadata-edit-${field.key}`}
                      onClick={() => setExpandedFieldKey(expanded ? null : field.key)}
                    >
                      <i className={expanded ? 'ri-close-line' : 'ri-pencil-line'} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="syidocs-table-icon-button is-danger"
                      aria-label={`Supprimer ${field.label}`}
                      title="Supprimer"
                      onClick={() => {
                        onChange(fields.filter((_, i) => i !== index));
                        if (expanded) setExpandedFieldKey(null);
                      }}
                    >
                      <RiDeleteBin6Line aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
              {expanded && <tr id={`metadata-edit-${field.key}`} className="metadata-edit-row">
                <td colSpan="6">
                  <div className="metadata-field-editor">
                    <h6>Modifier « {field.label} »</h6>
                    <FieldSettings
                      field={field}
                      fields={fields}
                      prefix={`field-${field.key}`}
                      onChange={(partial) => onChange(fields.map((item, i) => i === index ? { ...item, ...partial } : item))}
                    />
                  </div>
                </td>
              </tr>}
            </Fragment>;
          })}
          {!fields.length && <tr><td colSpan="6" className="metadata-table-empty">Aucune métadonnée définie. Ajoutez un premier champ.</td></tr>}
        </tbody>
      </table>
    </div>
    <div className="metadata-add-actions">
      <button type="button" className="btn btn-primary" onClick={() => openAddModal()}><RiAddLine aria-hidden="true" /> Ajouter une métadonnée</button>
      <div className="metadata-templates"><span>Partir d’un exemple :</span>{FIELD_TEMPLATES.map((template) => <button type="button" className="btn btn-sm" key={template.label} onClick={() => openAddModal(template)}>{template.label}</button>)}</div>
    </div>
    <section className="metadata-field-card"><h6>Tester sur un document</h6><p>PDF ou image (JPG, PNG, TIFF, BMP, WebP), 20 Mo maximum. Ce test n’enregistre aucune archive.</p>
      <input aria-label="Document exemple" type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.bmp,.webp,application/pdf,image/jpeg,image/png,image/tiff,image/bmp,image/webp" disabled={loading} onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); setError(''); }} />
      <button type="button" className="btn" disabled={!file || !fields.length || loading || !ocrEnabled} onClick={testCapture}>{loading ? 'Analyse en cours…' : 'Tester l’extraction'}</button>
      {error && <p role="alert" className="archive-create-error">{error}</p>}
      {result && testedFields !== JSON.stringify(fields) && <p role="status">Les règles ont changé. Relancez le test pour actualiser les résultats.</p>}
      {result && <MetadataReview fields={JSON.parse(testedFields)} values={result.metadata} logs={result.captureLogs} file={file} analyzed />}
    </section>
    <Modal isOpen={addModalOpen} toggle={closeAddModal} size="lg" className="modal-close metadata-add-modal">
      <ModalHeader toggle={closeAddModal}>Nouvelle métadonnée</ModalHeader>
      <ModalBody>
        <FieldSettings field={newField} fields={fields} prefix="new-metadata" onChange={(partial) => setNewField((current) => ({ ...current, ...partial }))} />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" type="button" onClick={closeAddModal}>Annuler</Button>
        <Button color="primary" type="button" onClick={addField}><RiAddLine aria-hidden="true" /> Ajouter la métadonnée</Button>
      </ModalFooter>
    </Modal>
  </div>;
};
